'use client';

import { Sparkles, Box } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { SettingsCard, PickerRow } from '@/shared/ui';
import type { RenderModelFamily } from '@/types/render';

export interface ModelContentProps {
  selectedModelFamily: RenderModelFamily;
  onModelFamilyChange: (family: RenderModelFamily) => void;
  // Layout variant — kept for API parity; both variants share the Apple-grouped
  // row idiom now.
  variant?: 'default' | 'compact';
  closeOnSelect?: () => void;
}

/**
 * Model-family picker (AURORA / ATLAS). Rendered from its OWN universal settings
 * card in `ScrollableRenderSettings`, shown to EVERYONE — model_selection is now
 * a base feature server-side (like output_style, Facade Pack pivot), so picking
 * the render engine is a creative control for all users, not a Builder perk.
 * Every model is selectable here — no lock/paywall path.
 */
export function ModelContent({
  selectedModelFamily,
  onModelFamilyChange,
  closeOnSelect,
}: ModelContentProps) {
  const t = useTranslations('render.settings.model');

  const models: Array<{
    type: RenderModelFamily;
    icon: typeof Sparkles;
  }> = [
    {
      type: 'AURORA',
      icon: Sparkles,
    },
    {
      type: 'ATLAS',
      icon: Box,
    },
  ];

  const handleSelect = (model: (typeof models)[number]) => {
    onModelFamilyChange(model.type);
    closeOnSelect?.();
  };

  return (
    <div className="space-y-4">
      {/* Apple ModelPickerView parity: grouped card with leading icon + title +
          description + trailing checkmark. No section footer. */}
      <SettingsCard>
        {models.map((model) => {
          const isSelected = selectedModelFamily === model.type;
          const Icon = model.icon;

          const leading = (
            <Icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
          );

          return (
            <PickerRow
              key={model.type}
              title={t(`types.${model.type}.name`)}
              description={t(`types.${model.type}.description`)}
              leading={leading}
              isSelected={isSelected}
              onClick={() => handleSelect(model)}
            />
          );
        })}
      </SettingsCard>
    </div>
  );
}

// Helper to get subtitle for preview
export function getModelSubtitle(
  selectedModelFamily: RenderModelFamily,
  t: (key: string) => string
): string {
  return t(`types.${selectedModelFamily}.name`);
}
